import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Sparkles, AlertTriangle, Shield, Lock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { ModePreview } from '../Payment/ModePreview';
import { createPayment, redirectToPayment } from '../../services/paymentService';
import { useToast } from '../../contexts/ToastContext';
interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPlan: 'free' | 'pay-per-scan' | 'pro';
}
const modalVariants = {
  hidden: {
    opacity: 0,
    scale: 0.95
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1],
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: {
      duration: 0.2
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 15
    }
  }
};
const PricingFeature = ({
  text,
  highlight = false
}: {
  text: string;
  highlight?: boolean;
}) => {
  return <li className="flex items-start gap-3" data-id="element-1939">
      <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center mt-0.5" data-id="element-1940">
        <Check size={18} className={highlight ? 'text-[#FF0000]' : 'text-gray-500'} strokeWidth={3} data-id="element-1941" />
      </div>
      <span className={`text-sm font-bold ${highlight ? 'text-white' : 'text-gray-400'}`} data-id="element-1942">
        {text}
      </span>
    </li>;
};
export function SubscriptionModal({
  isOpen,
  onClose,
  currentPlan
}: SubscriptionModalProps) {
  const {
    user
  } = useAuth();
  const {
    showToast
  } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleSelectPlan = async (plan: 'pay-per-scan' | 'pro') => {
    if (!user?.email || !user?.id) {
      setError('You must be logged in to purchase a plan');
      showToast('You must be logged in to purchase a plan', 'error');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Map plan to correct plan_type
      const planType = plan === 'pay-per-scan' ? 'single' : 'pro';
      // Call payment service (handles Authorization header automatically)
      const response = await createPayment(planType);
      // Redirect to Paystack in same window (_self)
      redirectToPayment(response.authorization_url);
    } catch (err: any) {
      console.error('Payment error:', err);
      const errorMessage = err.message || 'Payment server busy, try again.';
      setError(errorMessage);
      showToast(errorMessage, 'error');
      setLoading(false);
    }
  };
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-1943">
      {isOpen && <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto" data-id="element-1944">
          <motion.div className="bg-[#050505] border border-gray-800 max-w-7xl w-full p-8 my-8 max-h-[90vh] overflow-y-auto shadow-2xl relative" variants={modalVariants} initial="hidden" animate="visible" exit="exit" data-id="element-1945">
            {/* Corner Brackets */}
            <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-[#FF0000]" data-id="element-1946" />
            <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-[#FF0000]" data-id="element-1947" />
            <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-[#FF0000]" data-id="element-1948" />
            <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-[#FF0000]" data-id="element-1949" />

            {/* Scanning Line Animation */}
            <style data-id="element-1950">{`
              @keyframes scan {
                0%, 100% { transform: translateX(-100%); }
                50% { transform: translateX(100%); }
              }
            `}</style>
            <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#FF0000] to-transparent animate-[scan_4s_ease-in-out_infinite]" data-id="element-1951" />

            <div className="flex items-start justify-between mb-8" data-id="element-1952">
              <div data-id="element-1953">
                <h2 className="text-3xl md:text-4xl font-black text-white mb-2 uppercase tracking-tight" data-id="element-1954">
                  Invest In Your <span className="text-[#FFD700]" data-id="element-1955">Sanity</span>
                </h2>
                <p className="text-gray-400 text-base font-mono" data-id="element-1956">
                  Cheaper than therapy. Faster than a breakdown.
                </p>
              </div>
              <motion.button onClick={onClose} className="p-2 hover:bg-gray-900 border border-transparent hover:border-gray-800 transition-colors" whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} data-id="element-1957">
                <X size={24} className="text-gray-500" data-id="element-1958" />
              </motion.button>
            </div>

            {error && <motion.div className="bg-[#FF0000]/10 border border-[#FF0000]/30 p-4 mb-6 flex items-start gap-3" initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} data-id="element-1959">
                <AlertTriangle className="text-[#FF0000] flex-shrink-0" size={20} data-id="element-1960" />
                <p className="text-[#FF0000] text-sm font-mono uppercase tracking-wide" data-id="element-1961">
                  {error}
                </p>
              </motion.div>}

            <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto items-stretch" variants={modalVariants} data-id="element-1962">
              {/* Free Plan */}
              <motion.div className="bg-gray-900/50 border border-gray-800 p-8 relative hover:border-gray-600 transition-colors flex flex-col" variants={cardVariants} whileHover={{
            y: -4
          }} data-id="element-1963">
                <div className="absolute top-0 left-0 w-full h-1 bg-gray-700" data-id="element-1964" />

                <div className="flex items-center justify-between mb-6" data-id="element-1965">
                  <h3 className="text-2xl font-bold text-white uppercase tracking-wide" data-id="element-1966">
                    The "Just Curious"
                  </h3>
                  {currentPlan === 'free' && <span className="bg-gray-800 text-gray-300 text-xs font-bold px-3 py-1 uppercase tracking-wider" data-id="element-1967">
                      Trial
                    </span>}
                </div>

                <div className="flex items-baseline mb-8" data-id="element-1968">
                  <span className="text-5xl font-black text-white" data-id="element-1969">$0</span>
                  <span className="text-gray-500 ml-2 font-mono" data-id="element-1970">
                    / forever
                  </span>
                </div>

                <ul className="space-y-4 mb-8 flex-1" data-id="element-1971">
                  <PricingFeature text="1 Free Forensic Scan" data-id="element-1972" />
                  <PricingFeature text="Basic Toxicity Detection" data-id="element-1973" />
                  <PricingFeature text="Teaser Results Card" data-id="element-1974" />
                  <PricingFeature text="Standard Processing Speed" data-id="element-1975" />
                </ul>

                {/* Analysis Modes Preview */}
                <div className="mb-6 p-4 bg-gray-800/50 border border-gray-700" data-id="element-1976">
                  <p className="text-xs font-semibold text-gray-400 mb-3 uppercase tracking-widest" data-id="element-1977">
                    Analysis Modes:
                  </p>
                  <ModePreview planType="free" selectedMode="default" compact data-id="element-1978" />
                </div>

                <motion.button disabled={currentPlan === 'free'} onClick={onClose} className="w-full bg-gray-800 text-white py-4 font-bold uppercase tracking-wider hover:bg-gray-700 transition-colors border border-gray-700 disabled:opacity-50 disabled:cursor-not-allowed" whileHover={currentPlan !== 'free' ? {
              scale: 1.02
            } : {}} whileTap={currentPlan !== 'free' ? {
              scale: 0.98
            } : {}} data-id="element-1979">
                  {currentPlan === 'free' ? 'Current Plan' : 'Start Free Scan'}
                </motion.button>
              </motion.div>

              {/* Pay Per Scan - Most Popular */}
              <motion.div className="bg-gray-900/70 border-2 border-[#FFD700] p-8 relative shadow-[0_0_30px_rgba(255,215,0,0.15)] flex flex-col" variants={cardVariants} whileHover={{
            y: -4,
            boxShadow: '0 0 40px rgba(255, 215, 0, 0.25)'
          }} data-id="element-1980">
                {/* Most Popular Badge */}
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#FFD700] text-black font-black px-6 py-2 uppercase tracking-wider shadow-lg border-2 border-black text-center flex items-center gap-2" data-id="element-1981">
                  <Sparkles size={14} fill="black" data-id="element-1982" />
                  <span className="text-sm" data-id="element-1983">Most Popular</span>
                </div>

                <div className="flex items-center justify-between mb-6 mt-4" data-id="element-1984">
                  <h3 className="text-2xl font-bold text-white uppercase tracking-wide" data-id="element-1985">
                    Pay Per Scan
                  </h3>
                  {currentPlan === 'pay-per-scan' && <span className="bg-[#FFD700]/20 text-[#FFD700] text-xs font-bold px-3 py-1 uppercase tracking-wider border border-[#FFD700]/30" data-id="element-1986">
                      Active
                    </span>}
                </div>

                <div className="flex items-baseline mb-8" data-id="element-1987">
                  <span className="text-5xl font-black text-white" data-id="element-1988">$2.99</span>
                  <span className="text-gray-400 ml-2 font-mono" data-id="element-1989">/ scan</span>
                </div>

                <ul className="space-y-4 mb-8 flex-1" data-id="element-1990">
                  <PricingFeature text="1 Scan Included" data-id="element-1991" />
                  <PricingFeature text="Full AI Insights & Analysis" data-id="element-1992" />
                  <PricingFeature text="All Advanced Analysis Modes" data-id="element-1993" />
                  <PricingFeature text="2 Weekly Insights Delivered" data-id="element-1994" />
                  <PricingFeature text="Monthly Report Access" data-id="element-1995" />
                  <PricingFeature text="Shareable Results" data-id="element-1996" />
                  <PricingFeature text="Detailed Pattern Labels" data-id="element-1997" />
                  <PricingFeature text="Personalized Advice" data-id="element-1998" />
                </ul>

                {/* Analysis Modes Preview */}
                <div className="mb-6 p-4 bg-[#FFD700]/5 border border-[#FFD700]/20" data-id="element-1999">
                  <p className="text-xs font-semibold text-[#FFD700] mb-3 uppercase tracking-widest" data-id="element-2000">
                    Analysis Modes:
                  </p>
                  <ModePreview planType="pay-per-scan" selectedMode="relationships" compact data-id="element-2001" />
                </div>

                <motion.button onClick={() => handleSelectPlan('pay-per-scan')} disabled={currentPlan === 'pay-per-scan' || loading} className="w-full bg-[#FFD700] text-black py-4 font-black uppercase tracking-wider hover:bg-[#FFD700]/90 transition-all shadow-[0_0_15px_rgba(255,215,0,0.3)] hover:shadow-[0_0_25px_rgba(255,215,0,0.5)] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" whileHover={currentPlan !== 'pay-per-scan' && !loading ? {
              scale: 1.02
            } : {}} whileTap={currentPlan !== 'pay-per-scan' && !loading ? {
              scale: 0.98
            } : {}} data-id="element-2002">
                  {loading ? <>
                      <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" data-id="element-2003" />
                      <span data-id="element-2004">Redirecting...</span>
                    </> : currentPlan === 'pay-per-scan' ? 'Current Plan' : 'Get Started'}
                </motion.button>
              </motion.div>

              {/* Pro Plan */}
              <motion.div className="bg-black border-2 border-[#FF0000] p-8 relative shadow-[0_0_40px_rgba(255,0,0,0.15)] flex flex-col" variants={cardVariants} whileHover={{
            y: -4,
            boxShadow: '0 0 60px rgba(255, 0, 0, 0.25)'
          }} data-id="element-2005">
                {/* Discount Badge */}
                <div className="absolute -top-4 right-6 bg-[#FF0000] text-white font-black px-4 py-1.5 uppercase tracking-wider shadow-lg text-xs border border-black" data-id="element-2006">
                  65% OFF
                </div>

                <div className="flex items-center justify-between mb-6 mt-2" data-id="element-2007">
                  <h3 className="text-2xl font-black text-[#FF0000] uppercase tracking-wide" data-id="element-2008">
                    The "Truth Seeker"
                  </h3>
                  <Shield className="text-[#FF0000]" size={28} data-id="element-2009" />
                </div>

                <div className="flex items-baseline mb-2" data-id="element-2010">
                  <span className="text-5xl font-black text-white" data-id="element-2011">$9.99</span>
                  <span className="text-gray-400 ml-2 font-mono" data-id="element-2012">/ month</span>
                </div>
                <p className="text-[#FFD700] text-sm font-bold mb-8 uppercase tracking-wide" data-id="element-2013">
                  Normally $29.99/mo
                </p>

                <div className="space-y-4 mb-8 flex-1" data-id="element-2014">
                  <div className="bg-[#FF0000]/10 border border-[#FF0000]/30 p-4 mb-4" data-id="element-2015">
                    <p className="text-[#FF0000] text-xs font-bold uppercase mb-3 tracking-wider" data-id="element-2016">
                      INCLUDES "THE SHIELD" MONITORING
                    </p>
                    <ul className="space-y-3" data-id="element-2017">
                      <PricingFeature text="Unlimited Forensic Scans" highlight data-id="element-2018" />
                      <PricingFeature text="Persona Evolution Tracking" highlight data-id="element-2019" />
                      <PricingFeature text="Monthly Clarity Reports" highlight data-id="element-2020" />
                      <PricingFeature text="Gaslight Alert System" highlight data-id="element-2021" />
                    </ul>
                  </div>
                  <PricingFeature text="Priority Processing Queue" data-id="element-2022" />
                  <PricingFeature text="Full PDF Case Files" data-id="element-2023" />
                </div>

                {/* Analysis Modes Preview */}
                <div className="mb-6 p-4 bg-white/5 border border-white/10 backdrop-blur-sm" data-id="element-2024">
                  <p className="text-xs font-semibold text-white mb-3 uppercase tracking-widest" data-id="element-2025">
                    Analysis Modes:
                  </p>
                  <ModePreview planType="pro" selectedMode="relationships" compact light data-id="element-2026" />
                </div>

                <motion.button onClick={() => handleSelectPlan('pro')} disabled={currentPlan === 'pro' || loading} className="w-full bg-[#FF0000] text-white py-4 font-black uppercase tracking-wider hover:bg-red-600 transition-all shadow-[0_0_20px_rgba(255,0,0,0.4)] hover:shadow-[0_0_30px_rgba(255,0,0,0.6)] hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center justify-center gap-2" whileHover={currentPlan !== 'pro' && !loading ? {
              scale: 1.02
            } : {}} whileTap={currentPlan !== 'pro' && !loading ? {
              scale: 0.98
            } : {}} data-id="element-2027">
                  {loading ? <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2028" />
                      <span data-id="element-2029">Redirecting...</span>
                    </> : currentPlan === 'pro' ? 'Current Plan' : 'Get Full Access'}
                </motion.button>

                <div className="mt-4 text-center" data-id="element-2030">
                  <span className="text-xs text-gray-500 font-mono flex items-center justify-center gap-1.5" data-id="element-2031">
                    <Lock size={12} data-id="element-2032" /> 30-Day Money Back Guarantee
                  </span>
                </div>
              </motion.div>
            </motion.div>

            <motion.div className="mt-8 text-center" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.5
        }} data-id="element-2033">
              <p className="text-sm text-gray-500 font-mono" data-id="element-2034">
                🔒 Secure payment via Paystack • Money-back guarantee • Cancel
                anytime
              </p>
            </motion.div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}